import * as React from 'react';
import { cn } from '@/lib/utils';
import { Autocomplete, TextField } from '@mui/material';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  options: (SelectOption | string)[];
  icon?: React.ReactNode;
  onChange?: (e: any) => void;
  value?: any;
  allowCustom?: boolean;
}

const Select = React.forwardRef<any, SelectProps>(
  ({ className, label, error, hint, placeholder, options, icon, onChange, value, disabled, allowCustom = false, ...props }, ref) => {
    const normalizedOptions: SelectOption[] = React.useMemo(() => {
      return (options || []).map((opt) =>
        typeof opt === 'string' ? { value: opt, label: opt } : opt
      );
    }, [options]);

    const [internalValue, setInternalValue] = React.useState<string>(value ?? '');

    React.useEffect(() => {
      if (value !== undefined && value !== null) {
        setInternalValue(String(value));
      } else {
        setInternalValue('');
      }
    }, [value]);

    const triggerChange = (newVal: string) => {
      setInternalValue(newVal);
      if (onChange) {
        const event = {
          target: { name: (props as any).name, value: newVal },
          currentTarget: { name: (props as any).name, value: newVal },
        };
        onChange(event);
      }
    };

    const selectedOption = React.useMemo(() => {
      if (!internalValue) return null;
      const found = normalizedOptions.find(
        (o) => String(o.value) === String(internalValue) || String(o.label) === String(internalValue)
      );
      if (found) return found;
      if (allowCustom) {
        return { value: internalValue, label: internalValue };
      }
      return null;
    }, [normalizedOptions, internalValue, allowCustom]);

    return (
      <div className={cn("space-y-1.5 w-full", className)}>
        {label && (
          <label className="block text-xs font-bold text-gray-700 mb-1">
            {label.endsWith('*') ? (
              <>
                {label.slice(0, -1).trim()} <span className="text-red-500">*</span>
              </>
            ) : (
              label
            )}
          </label>
        )}

        {/* Hidden select to forward ref for react-hook-form validation focusing and value reading */}
        <select
          ref={ref}
          name={(props as any).name}
          value={internalValue}
          onChange={() => {}}
          className="hidden"
          style={{ display: 'none' }}
        >
          <option value="" />
          {normalizedOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <Autocomplete
          freeSolo={allowCustom}
          options={normalizedOptions}
          getOptionLabel={(opt) => {
            if (typeof opt === 'string') return opt;
            return opt?.label ?? '';
          }}
          isOptionEqualToValue={(opt, val) => {
            if (!opt || !val) return false;
            const optVal = typeof opt === 'string' ? opt : opt.value;
            const valVal = typeof val === 'string' ? val : val.value;
            return String(optVal) === String(valVal);
          }}
          value={selectedOption}
          onChange={(_, val) => {
            if (!val) {
              triggerChange('');
            } else if (typeof val === 'string') {
              triggerChange(val);
            } else {
              triggerChange(val.value);
            }
          }}
          onInputChange={(_, newInputValue, reason) => {
            if (allowCustom && (reason === 'input' || reason === 'clear')) {
              triggerChange(newInputValue);
            }
          }}
          disabled={disabled}
          clearOnEscape
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder || '-- Chọn --'}
              error={!!error}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: 'var(--bg-1)',
                  minHeight: '42px',
                  padding: '0 8px 0 12px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  color: 'var(--text-1)',
                  display: 'flex',
                  alignItems: 'center',
                  '& input': {
                    padding: '0 !important',
                    height: '42px',
                    boxSizing: 'border-box',
                  },
                  '& fieldset': { borderColor: error ? '#ef4444' : 'var(--border-c)' },
                  '&:hover fieldset': { borderColor: error ? '#ef4444' : 'var(--brand)' },
                  '&.Mui-focused fieldset': { borderColor: error ? '#ef4444' : 'var(--brand)', borderWidth: '2px' },
                }
              }}
            />
          )}
        />

        {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
        {hint && !error && <p className="text-[11px] text-[var(--text-3)] mt-1 ml-1">{hint}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
